import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, switchMap } from 'rxjs/operators';
import { DateTime } from 'luxon';

import { RequestMiscDomainService } from '@apps/its2/domain/request-misc-domain.service';
import { RequestsSummaryCounter } from '@common/models';

import * as fromBckpDashboardActions from './dashboard.actions';
import { Status } from '../../domain/models/status';
import { StatusPerDay } from '../../domain/models/status-per-day';
import { StatusPerRange, StatusPerPolicyType, PolicyTypeStatusRate, Variations, TotalCapacity, CountServers, ImagesPerDay, ServerChangesState } from './models';
import { SuccessRateListDomainService, CapacityListDomainService } from '../../domain';
import { WorkloadsMiscDomainService } from '../../domain/workloads-misc-domain.service';
import { HostDailySeries } from '../../domain/models/image-per-day';
import { ServerChanges } from '../../domain/models/server-change';
import { BckpConstants } from '../../bckp.constants';

@Injectable()
export class BckpDashboardEffects {
    constructor(
        private actions: Actions,
        private successRateListDomainService: SuccessRateListDomainService,
        private capacityListDomainService: CapacityListDomainService,
        private requestMiscDomainService: RequestMiscDomainService,
        private workloadsMiscDomainService: WorkloadsMiscDomainService
    ) { }

    loadBackupDashboardLastSessionRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardLastSessionRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.successRateListDomainService.getLastSession(params.since, params.until)
                    .pipe(
                        map((statuses: Status[]) =>
                            fromBckpDashboardActions.LoadBackupDashboardLastSessionSucceeded({
                                lastSession: {
                                    since: params.since,
                                    until: params.until,
                                    statuses: statuses,
                                    total: statuses[0].counter + statuses[1].counter + statuses[2].counter,
                                    expirationDate: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime })
                                }
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardStatusPerRangeRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardStatusPerRangeRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.successRateListDomainService.getStatusPerRange(params.since, params.until)
                    .pipe(
                        map((statusPerDay: StatusPerDay[]) =>
                            fromBckpDashboardActions.LoadBackupDashboardStatusPerRangeSucceeded({
                                statusPerRange: {
                                    since: params.since,
                                    until: params.until,
                                    statusPerDay: statusPerDay,
                                    total: 0,
                                    expirationDate: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime })
                                } as StatusPerRange
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardTicketingRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardTicketingRequested),
            switchMap(() =>
                this.requestMiscDomainService.getBckpSummary()
                    .pipe(
                        map((requestsSummaryCounter: RequestsSummaryCounter) =>
                            fromBckpDashboardActions.LoadBackupDashboardTicketingSucceeded({
                                ticketing: requestsSummaryCounter
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardStatusPerPolicyTypeRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardStatusPerPolicyTypeRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.successRateListDomainService.getStatusPerPolicyType(params.since, params.until)
                    .pipe(
                        map((sppt: import('../../domain/models').StatusPerPolicyType[]) =>
                            fromBckpDashboardActions.LoadBackupDashboardStatusPerPolicyTypeSucceeded({
                                statusPerPolicyType: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    policyTypeStatusRates: sppt.map((item: import('../../domain/models').StatusPerPolicyType) =>
                                        ({
                                            name: item.policyType,
                                            successRate: item.successRate
                                        }) as PolicyTypeStatusRate
                                    )
                                } as StatusPerPolicyType
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardVariationsRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardVariationsRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.capacityListDomainService.getVolumetricVariation(params.since, params.until)
                    .pipe(
                        map((volumetricVariations: import('../../domain/models').VolumetricVariation[]) =>
                            fromBckpDashboardActions.LoadBackupDashboardVariationsSucceeded({
                                variations: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    volumetricVariations: [...volumetricVariations]
                                } as Variations
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardTotalCapacityRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardTotalCapacityRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.capacityListDomainService.getTotalCapacity(params.since, params.until)
                    .pipe(
                        map((capacity: number) =>
                            fromBckpDashboardActions.LoadBackupDashboardTotalCapacitySucceeded({
                                totalCapacity: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    capacity: capacity
                                } as TotalCapacity
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardTotalServersRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardCountServersRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.capacityListDomainService.getCountServers(params.since, params.until)
                    .pipe(
                        map((countServers: number) =>
                            fromBckpDashboardActions.LoadBackupDashboardCountServersSucceeded({
                                countServers: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    count: countServers
                                } as CountServers
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardImagesPerDayRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardImagesPerDayRequested),
            switchMap((params: { days: number }) =>
                this.workloadsMiscDomainService.getImagesPerDay(params.days)
                    .pipe(
                        map((series: HostDailySeries[]) =>
                            fromBckpDashboardActions.LoadBackupDashboardImagesPerDaySucceeded({
                                imagesPerDay: {
                                    days: params.days,
                                    series: series,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime })
                                } as ImagesPerDay
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardServerChangesRequested = createEffect(() => this.actions
        .pipe(
            ofType(fromBckpDashboardActions.LoadBackupDashboardServerChangesRequested),
            switchMap((params: { days: number }) =>
                this.workloadsMiscDomainService.getServerChanges(params.days)
                    .pipe(
                        map((changes: ServerChanges) =>
                            fromBckpDashboardActions.LoadBackupDashboardServerChangesSucceeded({
                                serverChanges: {
                                    days: params.days,
                                    newServers: changes.newServers,
                                    disappearedServers: changes.disappearedServers,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime })
                                } as ServerChangesState
                            })
                        )
                    )
            )
        )
    );
}
